import { useMemo } from "react";
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridFilterItem,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { GridFilterInputMultipleSingleSelect } from "@mui/x-data-grid";

import { IContact } from "../types/Contact";
import { ITag } from "../types/Tag";
import { TableContactNameColumn } from "../components/TableContactNameColumn";
import { TableTagsColumn } from "../components/TableTagsColumn";
import { TableContactUserColumn } from "../components/TableContactUserColumn";
import { TableViewDetailsColumn } from "../components/TableViewDetailsColumn";
import { TagColorIndicator } from "../components/TagColorIndicator";
import { EnrichButton } from "../components/EnrichButton";
import MuiDatePicker from "../../../components/MuiDatePicker/MuiDatePicker";
import { handleDateFilterChange } from "../utils/dateFilterUtils";
import React from "react";

interface UseContactColumnsProps {
  teamTags: ITag[];
  onManageContactTags: (contact: IContact) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  teamMembersUser: any[];
  onOpenContactDrawer: (contactId: string, contactType: string) => void;
  onOpenUserDrawer: (contactId: string, handle: string) => void;
  teamId: number;
  showEnrichButton: boolean;
  refetchContactsData: () => void;
  handleOutOfCredits: () => unknown;
}

const nameColumn: GridColDef<IContact> = {
  field: "info",
  headerName: "Info",
  width: 250,
  getApplyQuickFilterFn: (value: string) => {
    const searchValue = value.toLowerCase();
    return (params: any) => {
      const searchString = `${params.full_name} ${params.email} ${
        params.job_title || ""
      } ${params.workplace || ""}`.toLowerCase();
      return searchString.includes(searchValue);
    };
  },
  filterOperators: getGridStringOperators().filter(
    operator => operator.value === "contains",
  ),
  renderCell: params => (
    <TableContactNameColumn
      contactInfo={params.row.info}
      enrichStatus={params.row.enrich_status}
    />
  ),
};

const createTagsColumn = (
  onManageContactTags: (contact: IContact) => void,
  activeTags: ITag[],
): GridColDef<IContact> => {
  return {
    field: "tags",
    headerName: "Tag",
    type: "singleSelect",
    filterOperators: [
      {
        label: "is any of",
        value: "isAnyOf",
        getApplyFilterFn: () => null,
        InputComponent: GridFilterInputMultipleSingleSelect,
        InputComponentProps: {
          renderOption: (props: any, option: any) => {
            const tagColor = activeTags.find(tag => tag.name === option)?.color;
            return (
              <Box {...props} display="flex" gap={1} key={option}>
                <TagColorIndicator color={tagColor || ""} />
                {option}
              </Box>
            );
          },
        },
      },
    ],
    width: 200,
    valueOptions: activeTags.map(tag => tag.name),
    renderCell: params => (
      <TableTagsColumn
        contact={params.row}
        onManageContactTags={onManageContactTags}
      />
    ),
  };
};

const createDateColumn = (
  filterModel: GridFilterModel,
  onFilterModelChange: (model: GridFilterModel) => void,
): GridColDef<IContact> => ({
  field: "created",
  headerName: "Date",
  type: "date",
  width: 160,
  valueGetter: (value: string) => new Date(value),
  filterOperators: [
    {
      label: "in between",
      value: "inBetween",
      getApplyFilterFn: () => null,
      InputComponent: MuiDatePicker,
      InputComponentProps: {
        value: filterModel.items.find(
          (item: GridFilterItem) =>
            item.field === "created" && item.operator === "inBetween",
        )?.value as Date[],
        handleRangeChange: (range?: DateRange) => {
          onFilterModelChange(handleDateFilterChange(range, filterModel));
        },
      },
    },
    {
      label: "before",
      value: "before",
      getApplyFilterFn: () => null,
      InputComponent: MuiDatePicker,
      InputComponentProps: {
        mode: "single",
        value: filterModel.items.find(
          (item: GridFilterItem) =>
            item.field === "created" && item.operator === "before",
        )?.value as Date,
        placeholder: "Select date",
        handleDateChange: (date?: Date) => {
          onFilterModelChange(
            handleDateFilterChange(date, filterModel, "created", "before"),
          );
        },
      },
    },
    {
      label: "after",
      value: "after",
      getApplyFilterFn: () => null,
      InputComponent: MuiDatePicker,
      InputComponentProps: {
        mode: "single",
        value: filterModel.items.find(
          (item: GridFilterItem) =>
            item.field === "created" && item.operator === "after",
        )?.value as Date,
        placeholder: "Select date",
        handleDateChange: (date?: Date) => {
          onFilterModelChange(
            handleDateFilterChange(date, filterModel, "created", "after"),
          );
        },
      },
    },
  ],
  renderCell: params => {
    return format(params.value, "MMM dd, yyyy");
  },
});

const createUserColumn = (
  teamMembersUser: string[],
  teamId: number,
  onOpenUserDrawer: (contactId: string, handle: string) => void,
): GridColDef<IContact> => ({
  field: "user",
  headerName: "User",
  type: "singleSelect",
  width: 225,
  filterOperators: getGridSingleSelectOperators().filter(
    operator => operator.value === "is",
  ),
  valueOptions: teamMembersUser,
  renderCell: params => (
    <TableContactUserColumn
      contactUser={params.row.user}
      onOpenUserDrawer={onOpenUserDrawer}
    />
  ),
});

const phoneColumn: GridColDef<IContact> = {
  field: "phone",
  headerName: "Phone Number",
  hideable: false,
  filterable: true,
  valueGetter: (_value: any, row) => {
    return row.info?.phone ?? "";
  },

  filterOperators: getGridStringOperators().filter(
    operator => operator.value === "contains",
  ),
};

const enrichColumn = (
  showEnrichButton: boolean,
  refetchContactsData: () => void,
  handleOutOfCredits: () => unknown,
): GridColDef<IContact> => ({
  field: "enrich",
  type: "custom",
  align: "right",
  headerAlign: "right",
  headerName: "",
  width: 200,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  hideSortIcons: true,
  headerClassName: "no-separator",
  renderCell: (params: GridRenderCellParams<IContact>) => [
    <React.Fragment key={`actions-${params.row.id}`}>
      {showEnrichButton && (
        <EnrichButton
          contact={params.row}
          refetchContactsData={refetchContactsData}
          handleOutOfCredits={handleOutOfCredits}
        />
      )}
    </React.Fragment>,
  ],
});
const actionsColumn = (
  handleOpenContactDrawer: (contactId: string, contactType: string) => void,
): GridColDef<IContact> => ({
  field: "actions",
  type: "custom",
  flex: 1,
  align: "right",
  headerAlign: "right",
  headerName: "",
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  hideSortIcons: true,
  headerClassName: "no-separator",
  renderCell: (params: GridRenderCellParams<IContact>) => [
    <TableViewDetailsColumn
      key="view-details"
      contactId={params.row.id}
      contactType={params.row.type}
      onOpenContactDrawer={handleOpenContactDrawer}
    />,
  ],
});

export const useContactColumns = ({
  teamTags,
  onManageContactTags,
  filterModel,
  onFilterModelChange,
  teamMembersUser,
  onOpenContactDrawer,
  onOpenUserDrawer,
  teamId,
  showEnrichButton,
  refetchContactsData,
  handleOutOfCredits,
}: UseContactColumnsProps): GridColDef<IContact>[] => {
  const activeTags = useMemo(
    () => teamTags.filter(tag => tag.num_contacts > 0),
    [teamTags],
  );

  return useMemo(
    () => [
      nameColumn,
      createTagsColumn(onManageContactTags, activeTags),
      createDateColumn(filterModel, onFilterModelChange),
      createUserColumn(teamMembersUser, teamId, onOpenUserDrawer),
      phoneColumn,
      enrichColumn(showEnrichButton, refetchContactsData, handleOutOfCredits),
      actionsColumn(onOpenContactDrawer),
    ],
    [
      teamTags,
      onManageContactTags,
      filterModel,
      onFilterModelChange,
      teamMembersUser,
      activeTags,
      onOpenContactDrawer,
      onOpenUserDrawer,
      showEnrichButton,
      refetchContactsData,
    ],
  );
};
